/*!
Math.uuid.js (v1.4)
http://www.broofa.com
mailto:robert@broofa.com

Copyright (c) 2010 Robert Kieffer
Dual licensed under the MIT and GPL licenses.
*/

/*
 * Generate a random uuid.
 *
 * USAGE: UUID.generate(length, radix)
 *   length - the desired number of characters
 *   radix  - the number of allowable values for each character.
 *
 * EXAMPLES:
 *   // No arguments  - returns RFC4122, version 4 ID
 *   >>> UUID.generate()
 *   "92329D39-6F5C-4520-ABFC-AAB64544E172"
 *
 *   // One argument - returns ID of the specified length
 *   >>> UUID.generate(15)     // 15 character ID (default base=62)
 *   "VcydxgltxrVZSTV"
 *
 *   // Two arguments - returns ID of the specified length, and radix. (Radix must be <= 62)
 *   >>> UUID.generate(8, 2)  // 8 character ID (base=2)
 *   "01001010"
 *   >>> UUID.generate(8, 10) // 8 character ID (base=10)
 *   "47473046"
 *   >>> UUID.generate(8, 16) // 8 character ID (base=16)
 *   "098F4D35"
 */

export class UUID {
    // Private array of chars to use
    private static CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz".split(
        ""
    );

    static generate(len: number, radix = UUID.CHARS.length) {
        const chars = UUID.CHARS;
        const uuid = [];

        let i: number;

        if (len) {
            // Compact form
            for (i = 0; i < len; i += 1) {
                // eslint-disable-next-line no-bitwise
                uuid[i] = chars[0 | (Math.random() * radix)];
            }
        } else {
            // rfc4122, version 4 form
            let r;

            // rfc4122 requires these characters
            uuid[8] = uuid[13] = uuid[18] = uuid[23] = "-";
            uuid[14] = "4";

            // Fill in random data.  At i==19 set the high bits of clock sequence as
            // per rfc4122, sec. 4.1.5
            for (i = 0; i < 36; i += 1) {
                if (!uuid[i]) {
                    // eslint-disable-next-line no-bitwise
                    r = 0 | (Math.random() * 16);
                    // eslint-disable-next-line no-bitwise
                    uuid[i] = chars[i === 19 ? (r & 0x3) | 0x8 : r];
                }
            }
        }

        return uuid.join("");
    }

    // A more performant, but slightly bulkier, RFC4122v4 solution.  We boost performance
    // by minimizing calls to random()
    static generateFast() {
        const chars = UUID.CHARS;
        const uuid = [];

        let rnd = 0;
        let r;

        for (let i = 0; i < 36; i += 1) {
            if (i === 8 || i === 13 || i === 18 || i === 23) {
                uuid[i] = "-";
            } else if (i === 14) {
                uuid[i] = "4";
            } else {
                // eslint-disable-next-line no-bitwise
                if (rnd <= 0x02) {
                    rnd = (0x2000000 + Math.random() * 0x1000000) | 0;
                }
                // eslint-disable-next-line no-bitwise
                r = rnd & 0xf;
                // eslint-disable-next-line no-bitwise
                rnd = rnd >> 4;
                // eslint-disable-next-line no-bitwise
                uuid[i] = chars[i === 19 ? (r & 0x3) | 0x8 : r];
            }
        }

        return uuid.join("");
    }
}
